import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import {
  CAMPO_CONFIG,
  adicionarMotivo,
  alternarEncerra,
  inativarMotivo,
  listaDeMotivos,
  moverMotivo,
  reativarMotivo,
  renomearMotivo,
} from '@/leads/motivos';
import { atualizarCampoConfig, ouvirClinica } from '@/services/clinicasService';

/**
 * As duas listas de motivo do funil (VG): "Não agendou" e "Não fechou".
 *
 * ★ Adicionar, REORDENAR (os mais comuns em cima — o padrão de `SecaoCaptacao`),
 * marcar se o motivo ENCERRA o lead, e "remover" — que aqui é `ativo: false`:
 * o motivo some do select e continua legível onde já foi gravado. **Um id nunca
 * é apagado nem reaproveitado.**
 *
 * ★ A aba semeia da MESMA fonte que o `DialogoEtapa` lê (`config ?? PADRÃO`) —
 * a contradição entre a aba e o runtime foi o que fechou a clínica na VF-3.
 * Abrir e salvar sem tocar em nada grava exatamente o que a tela já usava.
 */
const LISTAS = [
  { etapa: 'nao_agendou', titulo: 'Não agendou', quem: 'a SDR', ajuda: 'Por que a SDR tocou e não agendou.' },
  { etapa: 'nao_fechou', titulo: 'Não fechou', quem: 'o negociador', ajuda: 'Por que o paciente compareceu e ainda não fechou.' },
];

function Lista({ etapa, titulo, quem, ajuda, lista, aoMudar, ocupado }) {
  const [novo, setNovo] = useState('');
  const [novoEncerra, setNovoEncerra] = useState(false);
  const [editando, setEditando] = useState(null); // id
  const [rotuloEdit, setRotuloEdit] = useState('');

  const adicionar = () => {
    if (!novo.trim()) return;
    aoMudar(adicionarMotivo(lista, novo, novoEncerra));
    setNovo('');
    setNovoEncerra(false);
  };

  return (
    <section className="secao" data-etapa={etapa}>
      <h3 className="secao-titulo">{titulo}</h3>
      <p className="secao-sub">
        {ajuda} Quem escolhe é <strong>{quem}</strong>. A ordem é a do select — deixe os mais
        comuns em cima. <strong>“Encerra”</strong> marca o motivo que dá o lead por perdido:
        é o que entra na conversão de valor e na oportunidade perdida.
      </p>

      <ul className="lista-motivos">
        {lista.map((m, i) => (
          <li key={m.id} className={m.ativo ? undefined : 'motivo-inativo'}>
            {editando === m.id ? (
              <>
                <input
                  aria-label={`Novo nome para ${m.rotulo}`}
                  value={rotuloEdit}
                  disabled={ocupado}
                  onChange={(e) => setRotuloEdit(e.target.value)}
                />
                <button type="button" className="btn-texto" disabled={ocupado}
                  onClick={() => { aoMudar(renomearMotivo(lista, m.id, rotuloEdit)); setEditando(null); }}>
                  salvar nome
                </button>
                <button type="button" className="btn-texto" onClick={() => setEditando(null)}>cancelar</button>
              </>
            ) : (
              <>
                <span className="motivo-rotulo">{m.rotulo}</span>
                <label className="campo-inline" htmlFor={`enc-${etapa}-${m.id}`}>
                  <input id={`enc-${etapa}-${m.id}`} type="checkbox" checked={m.encerra} disabled={ocupado || !m.ativo}
                    onChange={() => aoMudar(alternarEncerra(lista, m.id))} />
                  encerra
                </label>
                <button type="button" className="btn-texto" disabled={ocupado || i === 0}
                  title="Subir" onClick={() => aoMudar(moverMotivo(lista, m.id, -1))}>↑</button>
                <button type="button" className="btn-texto" disabled={ocupado || i === lista.length - 1}
                  title="Descer" onClick={() => aoMudar(moverMotivo(lista, m.id, 1))}>↓</button>
                <button type="button" className="btn-texto" disabled={ocupado}
                  onClick={() => { setEditando(m.id); setRotuloEdit(m.rotulo); }}>renomear</button>
                {m.ativo ? (
                  <button type="button" className="btn-texto perigo" disabled={ocupado}
                    title="Some do select; quem já tem este motivo continua com ele"
                    onClick={() => aoMudar(inativarMotivo(lista, m.id))}>remover</button>
                ) : (
                  <button type="button" className="btn-texto" disabled={ocupado}
                    onClick={() => aoMudar(reativarMotivo(lista, m.id))}>reativar</button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      <div className="linha">
        <div className="campo">
          <label htmlFor={`novo-${etapa}`}>Novo motivo</label>
          <input id={`novo-${etapa}`} value={novo} disabled={ocupado} placeholder="ex.: Mudou de cidade"
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionar(); } }} />
        </div>
        <label className="campo-inline" htmlFor={`novo-enc-${etapa}`}>
          <input id={`novo-enc-${etapa}`} type="checkbox" checked={novoEncerra} disabled={ocupado}
            onChange={(e) => setNovoEncerra(e.target.checked)} />
          encerra
        </label>
        <button type="button" className="btn-secundario" disabled={ocupado || !novo.trim()} onClick={adicionar}>
          Adicionar motivo
        </button>
      </div>
    </section>
  );
}

export default function SecaoMotivos() {
  const { clinicaId } = useAuth();
  const [clinica, setClinica] = useState(null);
  const [listas, setListas] = useState({ nao_agendou: null, nao_fechou: null });
  const [erro, setErro] = useState(null);
  const [salvo, setSalvo] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(clinicaId, setClinica, () => setErro('Não foi possível carregar a configuração.'));
  }, [clinicaId]);

  const config = clinica?.config ?? null;
  useEffect(() => {
    // ★ Semente da MESMA fonte do runtime: `listaDeMotivos(config, etapa)`.
    setListas({
      nao_agendou: listaDeMotivos(config, 'nao_agendou'),
      nao_fechou: listaDeMotivos(config, 'nao_fechou'),
    });
    setSalvo(false);
  }, [config]);

  const mudar = (etapa) => (lista) => { setListas((x) => ({ ...x, [etapa]: lista })); setSalvo(false); };

  async function salvar() {
    setErro(null);
    setOcupado(true);
    try {
      await atualizarCampoConfig(clinicaId, CAMPO_CONFIG.nao_agendou, listas.nao_agendou);
      await atualizarCampoConfig(clinicaId, CAMPO_CONFIG.nao_fechou, listas.nao_fechou);
      setSalvo(true);
    } catch (e) {
      setErro(e?.code === 'permission-denied' ? 'Só o gestor altera os motivos.' : (e.message ?? 'Não foi possível salvar.'));
    } finally {
      setOcupado(false);
    }
  }

  if (!listas.nao_agendou) return <p className="vazio">Carregando os motivos…</p>;

  return (
    <section className="secao">
      <h2 className="secao-titulo">Motivos do funil</h2>
      <p className="secao-sub">
        As duas colunas de “não” do funil exigem um motivo da lista. “Perdido” deixou de ser uma
        coluna: é um motivo que <strong>encerra</strong>.
      </p>
      {LISTAS.map((l) => (
        <Lista key={l.etapa} {...l} lista={listas[l.etapa]} aoMudar={mudar(l.etapa)} ocupado={ocupado} />
      ))}
      {erro && <p className="erro">{erro}</p>}
      <footer className="dialogo-acoes">
        {salvo && <span className="campo-ajuda">Salvo.</span>}
        <button type="button" className="btn-primario" disabled={ocupado} onClick={salvar}>
          {ocupado ? 'Salvando…' : 'Salvar motivos'}
        </button>
      </footer>
    </section>
  );
}
